// Shared Command-Center shell primitives (plan §P1.8 — UI Workflow
// Consistency Audit).
//
// Every center-pane surface (Briefing, Pipeline, G1–G5, Download
// Center) used to re-implement the same trio: a heading row with
// chips, an action bar with an indigo-gradient primary CTA, an inline
// "What you're approving" context block. After the audit catalogued
// 30+ ad-hoc instances of those patterns, we lift them into a single
// set of primitives that all use the canonical ``alm-*`` design
// language.
//
// The intent is *not* to be a full design system — these are the
// minimum primitives needed to delete the parallel shadcn/ad-hoc
// implementations the audit flagged in §8.1/§8.2. New code should
// reach for these helpers; legacy components are migrated
// incrementally so the visual jump from the Hub into the Command
// Center stops feeling like three different apps.

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

// --------------------------------------------------------------------------- //
// Section — a labelled card-style slab. Used to wrap "Constraint Log",
// "Document Map", "Decisions", "Outputs — verify before moving on", etc.
// --------------------------------------------------------------------------- //
export function Section({
  label,
  icon,
  trailing,
  children,
  className,
}: {
  /** Tiny uppercase eyebrow above the body. */
  label?: ReactNode
  /** Optional icon rendered next to the label. */
  icon?: ReactNode
  /** Right-aligned slot for chips, counts, or links. */
  trailing?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('mb-5', className)}>
      {(label || trailing) && (
        <div className="flex items-center justify-between gap-2 mb-2">
          {label ? (
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              {icon}
              <span>{label}</span>
            </div>
          ) : (
            <span />
          )}
          {trailing ? (
            <div className="flex items-center gap-1.5">{trailing}</div>
          ) : null}
        </div>
      )}
      {children}
    </section>
  )
}

// --------------------------------------------------------------------------- //
// Breadcrumb — eyebrow row above the focus-surface title (plan §P2.13).
//
// Tells the user where they are in the 5-phase / 5-gate ladder so the
// Command Center stops being a "no breadcrumb anywhere" black box
// (audit §3.2.2). Renders a thin arrow between trail entries; the
// last entry is brightened to mark the current location.
// --------------------------------------------------------------------------- //
export function Breadcrumb({
  trail,
  className,
}: {
  trail: ReactNode[]
  className?: string
}) {
  if (trail.length === 0) return null
  return (
    <nav aria-label="Breadcrumb" className={cn('alm-breadcrumb mb-1.5', className)}>
      {trail.map((entry, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 ? (
            <span aria-hidden className="alm-breadcrumb-sep">
              ›
            </span>
          ) : null}
          <span>{entry}</span>
        </span>
      ))}
    </nav>
  )
}

// --------------------------------------------------------------------------- //
// PageHeader — the chips-strip + title + subtitle block every focus
// surface starts with.
// --------------------------------------------------------------------------- //
export function PageHeader({
  breadcrumb,
  chips,
  title,
  subtitle,
  trailing,
}: {
  /** Optional breadcrumb trail rendered as the eyebrow row. */
  breadcrumb?: ReactNode[]
  /** Pre-built chip nodes (`alm-chip` spans). Renders a flex row above title. */
  chips?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  /** Right-aligned slot for Re-emit / Raw artifact buttons. */
  trailing?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="min-w-0">
        {breadcrumb && breadcrumb.length > 0 ? (
          <Breadcrumb trail={breadcrumb} />
        ) : null}
        {chips ? (
          <div className="flex flex-wrap items-center gap-2 mb-2">{chips}</div>
        ) : null}
        <h2 className="text-2xl font-bold tracking-tight text-slate-100">
          {title}
        </h2>
        {subtitle ? (
          <p className="text-sm text-slate-400 mt-1 max-w-xl">{subtitle}</p>
        ) : null}
      </div>
      {trailing ? (
        <div className="flex flex-col gap-1.5 shrink-0">{trailing}</div>
      ) : null}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// ActionBar — sticky-feel bottom row of decision buttons. Just a flex
// wrapper today; lifted into a primitive so future "scroll-into-view"
// or "pin to viewport bottom" behaviour lands in one place.
// --------------------------------------------------------------------------- //
export function ActionBar({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// PrimaryButton — the indigo-gradient "Approve & continue" / "Run
// Phase N" / "Finalize Export" button. Replaces the four-line inline
// gradient that every gate panel was hand-rolling.
// --------------------------------------------------------------------------- //
type PrimaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean
  icon?: ReactNode
}

export function PrimaryButton({
  children,
  className,
  loading,
  disabled,
  icon,
  ...rest
}: PrimaryButtonProps) {
  return (
    <button
      type={rest.type ?? 'button'}
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon ?? null}
      {children}
    </button>
  )
}

// --------------------------------------------------------------------------- //
// SecondaryButton — the "Re-emit" / "Raw artifact" / "Cancel" outline
// affordance. Kept lightweight on purpose; pages that need bespoke
// behaviour can still drop down to a raw <button>.
// --------------------------------------------------------------------------- //
type SecondaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean
  icon?: ReactNode
  size?: 'sm' | 'md'
}

export function SecondaryButton({
  children,
  className,
  loading,
  disabled,
  icon,
  size = 'md',
  ...rest
}: SecondaryButtonProps) {
  const sizing =
    size === 'sm'
      ? 'gap-1.5 rounded-md px-2.5 py-1 text-xs'
      : 'gap-2 rounded-lg px-4 py-2 text-sm font-semibold'
  return (
    <button
      type={rest.type ?? 'button'}
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center border border-slate-700 text-slate-300 hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed',
        sizing,
        className,
      )}
    >
      {loading ? (
        <Loader2 className={size === 'sm' ? 'h-3 w-3 animate-spin' : 'h-4 w-4 animate-spin'} />
      ) : (
        icon ?? null
      )}
      {children}
    </button>
  )
}

// --------------------------------------------------------------------------- //
// DecisionContext — the inline "What you're approving" box every gate
// renders above its decision row. G2 had a private copy; G3 inlined a
// near-identical block. Lifting into a primitive lets G1/G4/G5 adopt
// the same affordance without copy-pasting the panel again.
// --------------------------------------------------------------------------- //
export function DecisionContext({
  label = "What you're approving",
  children,
  className,
}: {
  label?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'rounded-md border border-slate-700/50 bg-slate-950/40 p-3 mt-2 mb-1',
        className,
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
        {label}
      </div>
      <div className="text-[12.5px] leading-relaxed text-slate-300 [&>p]:m-0">
        {children}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// EmptyState — used when an artifact is missing, a gate is not visible
// at the current cadence, etc. Mirrors the dashed-border slate slab the
// audit flagged was inconsistent across surfaces.
// --------------------------------------------------------------------------- //
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'info',
  className,
}: {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  tone?: 'info' | 'warn'
  className?: string
}) {
  const palette =
    tone === 'warn'
      ? 'border-amber-700/50 bg-amber-950/20 text-amber-100'
      : 'border-slate-700/60 bg-slate-900/40 text-slate-300'
  return (
    <div
      className={cn(
        'rounded-md border border-dashed p-4 flex items-start gap-3',
        palette,
        className,
      )}
    >
      {icon ? <div className="shrink-0 mt-0.5">{icon}</div> : null}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-100">{title}</p>
        {description ? (
          <div className="text-xs text-slate-400 mt-1 leading-relaxed">
            {description}
          </div>
        ) : null}
        {action ? <div className="mt-3">{action}</div> : null}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// WhyThisGate — the dark-themed explainer block G1 + G2 + G3 already use.
// Lifted out so G4 and G5 can drop the same affordance without copying
// the markup yet again.
// --------------------------------------------------------------------------- //
export function WhyThisGate({
  icon,
  children,
  className,
}: {
  icon?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 flex gap-2 text-xs text-slate-400',
        className,
      )}
    >
      {icon ? <div className="shrink-0 mt-0.5">{icon}</div> : null}
      <div className="min-w-0">{children}</div>
    </div>
  )
}
